import type { FilaDdjjExcel } from '@lib/services/empresas.service';

/** Hoja de la plantilla con las preguntas (la otra es el instructivo). */
const HOJA_PREGUNTAS = 'Preguntas';

/**
 * Lee el Excel del cuestionario (la plantilla que baja el ABM, editada) y
 * devuelve las filas crudas tal como las espera `POST /empresas/ddjj/validar`:
 * un objeto por fila con las columnas de la plantilla como claves (la fila 1
 * es el encabezado) y `fila` con el número de Excel, para que los errores del
 * server se puedan ubicar en la planilla.
 *
 * No valida nada acá: eso lo hace el server, que es la única fuente de las
 * reglas. Las filas totalmente vacías se descartan (la plantilla trae filas en
 * blanco al final).
 */
export async function leerCuestionarioExcel(file: File): Promise<FilaDdjjExcel[]> {
  const XLSX = await import('xlsx');
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const nombre = wb.SheetNames.includes(HOJA_PREGUNTAS) ? HOJA_PREGUNTAS : wb.SheetNames[0];
  const ws = wb.Sheets[nombre];
  if (!ws) return [];
  // `raw: false` → los booleanos/números vienen como texto ("SI", "5"): es lo que
  // el validador del server entiende (booleanoDeExcel / Number).
  const filas = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '', raw: false });
  return filas
    .map((f, i) => ({ ...limpiarClaves(f), fila: i + 2 }))
    .filter((f) => Object.entries(f).some(([k, v]) => k !== 'fila' && String(v ?? '').trim() !== ''));
}

/** Los encabezados de la plantilla son las claves; se toleran espacios alrededor. */
function limpiarClaves(f: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(f)) out[k.trim()] = v;
  return out;
}
